import constant from "../../../config/constant";
import useIntlStore from "@/stores/useIntlStore";
import isString from "lodash/isString";
import isObject from "lodash/isObject";
import isArray from "lodash/isArray";
import lodashOmit from "lodash/omit";
import type { Option, Config, ConstantOption } from "./type";
import type { Locale } from "../../types/global";
import { getT } from "@/utils/intlUtils";

/**
 * @description config 默认配置
 * @name code 指定枚举
 * @name keyFormat string｜number 只适用Map,不支持Object
 * @name resFormat object｜map | arr | option 返回参数格式
 * @name omit string[] 去除不需要的字段
 */
const DEFAULT_OPTION: Config = {
  keyFormat: "string",
  resFormat: "map",
  omit: [],
  isAll: false,
};

/**
 * @description hook获取枚举数据
 * @name configs  Option[] | string[]
 */
export const useEnums = (configs: Config[] | string[] = []) => {
  const { locale } = useIntlStore();

  const transEnumsData = () => {
    const enums: Record<string, any> = {};

    configs.forEach((x) => {
      if (isString(x)) {
        const constantOptions = constant[x];
        if (!isArray(constantOptions)) {
          return;
        }
        enums[x] = getDiffFomartEnums(DEFAULT_OPTION, constantOptions, locale);
      } else if (isObject(x) && x.code) {
        const constantOptions = constant[x.code];

        if (!isArray(constantOptions)) {
          return;
        }

        enums[x.code] = getDiffFomartEnums(x, constantOptions, locale);
      }
    });

    return enums;
  };

  return transEnumsData();
};

const getDiffFomartEnums = (
  config: Config,
  constantOptions: ConstantOption[],
  locale: Locale,
) => {
  const {
    keyFormat = DEFAULT_OPTION["keyFormat"],
    resFormat = DEFAULT_OPTION["resFormat"],
    omit = DEFAULT_OPTION["omit"],
    isAll = DEFAULT_OPTION["isAll"],
  } = config;
  switch (resFormat) {
    case "map":
      return getMapEnums({ keyFormat, omit, isAll }, constantOptions, locale);
    case "object":
      return getObjectEnums(
        { keyFormat, omit, isAll },
        constantOptions,
        locale,
      );
    case "option":
      return getOptionEnums(
        { keyFormat, omit, isAll },
        constantOptions,
        locale,
      );
  }
};

const getMapEnums = (
  config: Config,
  constantOptions: ConstantOption[],
  locale: Locale,
) => {
  const { keyFormat, omit = [], isAll } = config;
  const map = new Map();

  if (isAll) {
    const t = getT();
    map.set("", t("全部"));
  }

  constantOptions.forEach((item) => {
    const current = {
      parent: item?.parent,
      text: item.label[locale],
    };
    if (keyFormat === "string") {
      map.set(item.value.toString(), current);
    } else if (keyFormat === "number") {
      map.set(Number(item.value), current);
    }
  });

  if (omit.length !== 0) {
    omit.forEach((x) => {
      map.delete(x);
    });
  }

  return map;
};

const getObjectEnums = (
  config: Config,
  constantOptions: ConstantOption[],
  locale: Locale,
) => {
  const { omit = [], isAll } = config;

  const enums: Record<string, any> = {};

  constantOptions.forEach((item) => {
    const current = {
      text: item.label[locale],
      parent: item.parent,
    };
    enums[item.value] = current;
  });

  if (omit.length !== 0) {
    return lodashOmit(enums, omit);
  }

  if (isAll) {
    const t = getT();
    enums[""] = {
      text: t("全部"),
    };
  }

  return enums;
};

const getOptionEnums = (
  config: Config,
  constantOptions: ConstantOption[],
  locale: Locale,
) => {
  const { keyFormat, omit = [], isAll } = config;
  const options: Option[] = [];

  constantOptions.forEach((item) => {
    const option: Option = {
      parent: item.parent,
      label: item.label[locale],
      value:
        keyFormat === "string" ? item.value.toString() : Number(item.value),
    };
    options.push(option);
  });

  if (omit.length !== 0) {
    const omitSet = new Set([...omit]);
    return options.filter((x) => !omitSet.has(x.value));
  }

  if (isAll) {
    const t = getT();
    options.push({ value: "", label: t("全部") });
  }

  return options;
};
